import React, {useState, useEffect, useRef, useCallback} from "react"
import {
	Box,
	Typography,
	CircularProgress,
	Button,
	Menu,
	MenuItem,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	Tooltip,
	Skeleton,
	Divider as MuiDivider,
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import LinkIcon from "@mui/icons-material/Link"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import {alpha} from "@mui/material/styles"
import styled, {keyframes} from "styled-components"
import {format, formatDistanceToNow, formatDistanceStrict} from "date-fns"
import {useSearchParams, useNavigate} from "react-router-dom"

import {AddReminderButton} from "../../AddReminderButton"
import {AccountStatusIcon} from "../Accounts/AccountStatusIcon"
import {NoteForm} from "../NoteForm"
import {NoteItemCard} from "../NotesV2"

import {CaseAttentionChip} from "./CaseAttentionChip"
import {findOpenEndedWait} from "./caseAttentionState"
import {ConversationSection} from "./ConversationSection"
import {OpenCaseModal} from "./OpenCaseModal"
import {ParkCaseModal} from "./ParkCaseModal"
import {RaiseJiraTicketDialog} from "./RaiseJiraTicketDialog"
import {ResolveCaseModal} from "./ResolveCaseModal"
import {ReturnCaseDialog} from "./ReturnCaseDialog"

import {api} from "@indebted/api"
import type {
	CasesResponse,
	ActiveCase,
	ActiveCaseAccount,
	CustomerAccount,
	CaseManual,
	ClosedCase,
	ResolvedAccountCase,
	ResolvedCaseAccount,
	ScaffoldCaseResponse,
	CaseNote,
} from "@indebted/api"
import {CopyButton} from "@indebted/components/CopyButton"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

const CLIENT_WAIT_PARTY = "CLIENT"

type CasesProps = {
	customerID: string
	clientName?: string
	customerName?: string
	customerRef?: string
	customerEmails?: string[]
	refreshCustomerData: (delayMs?: number) => void
	onShowManuals: (caseType: string, manuals: CaseManual[]) => void
}

function Cases({
	customerID,
	clientName,
	customerName,
	customerRef,
	customerEmails,
	refreshCustomerData,
	onShowManuals,
}: CasesProps) {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [searchParams, setSearchParams] = useSearchParams()
	const highlightedID = searchParams.get("CaseID")
	const [highlightPhase, setHighlightPhase] = useState<"blink" | "settled">("blink")
	const containerRef = useRef<HTMLDivElement>(null)
	const [loading, setLoading] = useState(true)
	const [casesData, setCasesData] = useState<CasesResponse | null>(null)
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [scaffoldData, setScaffoldData] = useState<ScaffoldCaseResponse | null>(null)
	const [scaffolding, setScaffolding] = useState(false)
	const [resolvingCase, setResolvingCase] = useState<{activeCase: ActiveCase; accounts?: ActiveCaseAccount[]} | null>(
		null,
	)
	const [parkingCase, setParkingCase] = useState<ActiveCase | null>(null)

	const loadCases = React.useCallback(() => {
		if (!agentEmail) return
		setLoading(true)
		api.customer
			.getCases(customerID, agentEmail)
			.then((data) => setCasesData(data))
			.catch(() => notification.error("Failed to load cases"))
			.finally(() => setLoading(false))
	}, [customerID, agentEmail, notification])

	useEffect(() => {
		loadCases()
	}, [loadCases])

	useEffect(() => {
		setHighlightPhase("blink")
		if (!loading && highlightedID && containerRef.current) {
			const el = containerRef.current.querySelector(`[data-case-id="${highlightedID}"]`)
			if (el) {
				el.scrollIntoView({behavior: "smooth", block: "center"})
				setTimeout(() => setHighlightPhase("settled"), 2500)
			}
		}
	}, [loading, highlightedID])

	const handleCaseTypeSelect = (caseTypeID: string) => {
		setAnchorEl(null)
		setScaffolding(true)
		api.customer
			.scaffoldCase(customerID, caseTypeID)
			.then((data) => setScaffoldData(data))
			.catch((error: {Message: string}) => notification.error(error.Message))
			.finally(() => setScaffolding(false))
	}

	const handleCaseSubmitted = () => {
		setScaffoldData(null)
		removeCaseIDParam()
		loadCases()
		refreshCustomerData(5000)
	}

	const removeCaseIDParam = () => {
		setSearchParams((prev) => {
			prev.delete("CaseID")
			return prev
		})
	}

	const handleCaseResolved = () => {
		setResolvingCase(null)
		removeCaseIDParam()
		loadCases()
		refreshCustomerData(5000)
	}

	const handleCaseParked = () => {
		setParkingCase(null)
		loadCases()
	}

	if (loading) {
		return (
			<Box p={3} display="flex" justifyContent="center">
				<CircularProgress />
			</Box>
		)
	}

	if (!casesData) {
		return (
			<Box p={3}>
				<Typography color="error">Failed to load cases</Typography>
			</Box>
		)
	}

	const {
		CustomerRef,
		DOB,
		TotalOutstanding,
		CustomerAccounts,
		ActiveCases,
		ClosedCases,
		ResolvedCases,
		CaseTypes,
		CaseOwnershipEnabled,
	} = casesData
	const resolvedAccountCases = ResolvedCases?.AccountCases ?? []
	const resolvedCustomerCases = ResolvedCases?.CustomerCases ?? []
	const totalResolved = ClosedCases.length + resolvedAccountCases.length

	// A conversation shown in an active case may also be linked to a case in any
	// bucket, so the cross-link labels must cover every case the customer has.
	const caseLabelByID: Record<string, string> = {}
	for (const c of [...ActiveCases, ...ClosedCases, ...resolvedCustomerCases, ...resolvedAccountCases]) {
		caseLabelByID[c.CaseID] = c.CaseType
	}

	return (
		<Box p={3} ref={containerRef}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h6">Cases</Typography>
				<Button
					variant="outlined"
					size="small"
					endIcon={scaffolding ? <CircularProgress size={16} /> : <KeyboardArrowDownIcon />}
					onClick={(e) => setAnchorEl(e.currentTarget)}
					disabled={scaffolding || CaseTypes.length === 0}
					sx={{textTransform: "none", fontSize: "0.875rem"}}
				>
					OPEN NEW CASE
				</Button>
				<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
					{CaseTypes.map((ct) => (
						<CaseTypeMenuItem key={ct.ID} onClick={() => handleCaseTypeSelect(ct.ID)}>
							{ct.Label}
							<KeyboardArrowRightIcon>&gt;</KeyboardArrowRightIcon>
						</CaseTypeMenuItem>
					))}
				</Menu>
			</Box>

			{ActiveCases.length === 0 && totalResolved === 0 && (
				<Typography color="textSecondary">No active case</Typography>
			)}

			{ActiveCases.length > 0 && (
				<Box mb={3}>
					<Typography variant="subtitle1" fontWeight={600} mb={2}>
						Active Cases ({ActiveCases.length})
					</Typography>
					<Box display="flex" flexDirection="column" gap={2}>
						{ActiveCases.map((c) => (
							<CaseCardWrapper
								key={c.CaseID}
								data-case-id={c.CaseID}
								$highlight={c.CaseID === highlightedID}
								$phase={highlightPhase}
							>
								<ActiveCaseCard
									customerID={customerID}
									customerRef={customerRef ?? CustomerRef}
									dob={DOB}
									totalOutstanding={TotalOutstanding}
									customerAccounts={CustomerAccounts}
									activeCase={c}
									clientName={clientName}
									customerName={customerName}
									customerEmails={customerEmails}
									caseLabelByID={caseLabelByID}
									onResolveCase={() => {
										const unresolved = c.Accounts.filter((a) => !a.Resolution)
										setResolvingCase({
											activeCase: c,
											accounts: unresolved.length > 0 ? unresolved : undefined,
										})
									}}
									onResolveAccount={(account) =>
										setResolvingCase({activeCase: c, accounts: [account]})
									}
									caseOwnershipEnabled={CaseOwnershipEnabled}
									onParkCase={() => setParkingCase(c)}
									onPickedUp={loadCases}
									onReleased={loadCases}
									onReturned={loadCases}
									onJiraTicketRaised={loadCases}
									onShowManuals={onShowManuals}
								/>
							</CaseCardWrapper>
						))}
					</Box>
				</Box>
			)}

			{ActiveCases.length > 0 && totalResolved > 0 && <Divider />}

			{totalResolved > 0 && (
				<Box>
					<Typography variant="subtitle1" fontWeight={600} mb={2}>
						Resolved Cases ({totalResolved})
					</Typography>
					<Box display="flex" flexDirection="column" gap={2}>
						{ClosedCases.map((c) => (
							<CaseCardWrapper
								key={c.CaseID}
								data-case-id={c.CaseID}
								$highlight={c.CaseID === highlightedID}
								$phase={highlightPhase}
							>
								<ClosedCaseCard customerID={customerID} closedCase={c} onArchived={loadCases} />
							</CaseCardWrapper>
						))}
						{resolvedAccountCases.map((c) => (
							<CaseCardWrapper
								key={c.CaseID}
								data-case-id={c.CaseID}
								$highlight={c.CaseID === highlightedID}
								$phase={highlightPhase}
							>
								<ResolvedAccountCaseCard
									customerID={customerID}
									resolvedCase={c}
									onArchived={loadCases}
								/>
							</CaseCardWrapper>
						))}
					</Box>
				</Box>
			)}

			{scaffoldData && (
				<OpenCaseModal
					customerID={customerID}
					scaffoldData={scaffoldData}
					onClose={() => setScaffoldData(null)}
					onSubmitted={handleCaseSubmitted}
				/>
			)}

			{resolvingCase && (
				<ResolveCaseModal
					customerID={customerID}
					activeCase={resolvingCase.activeCase}
					accounts={resolvingCase.accounts}
					onClose={() => setResolvingCase(null)}
					onResolved={handleCaseResolved}
				/>
			)}

			{parkingCase && (
				<ParkCaseModal
					customerID={customerID}
					activeCase={parkingCase}
					onClose={() => setParkingCase(null)}
					onParked={handleCaseParked}
				/>
			)}
		</Box>
	)
}

function ActiveCaseCard({
	customerID,
	customerRef,
	dob,
	totalOutstanding,
	customerAccounts,
	activeCase,
	clientName,
	customerName,
	customerEmails,
	caseLabelByID,
	onResolveCase,
	onResolveAccount,
	caseOwnershipEnabled,
	onParkCase,
	onPickedUp,
	onReleased,
	onReturned,
	onJiraTicketRaised,
	onShowManuals,
}: {
	customerID: string
	customerRef?: string
	dob?: string
	totalOutstanding?: string
	customerAccounts?: CustomerAccount[]
	activeCase: ActiveCase
	clientName?: string
	customerName?: string
	customerEmails?: string[]
	caseLabelByID: Record<string, string>
	onResolveCase: () => void
	onResolveAccount: (account: ActiveCaseAccount) => void
	caseOwnershipEnabled: boolean
	onParkCase: () => void
	onPickedUp: () => void
	onReleased: () => void
	onReturned: () => void
	onJiraTicketRaised: () => void
	onShowManuals: (caseType: string, manuals: CaseManual[]) => void
}) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [notes, setNotes] = useState<CaseNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)
	const [pickingUp, setPickingUp] = useState(false)
	const [releasing, setReleasing] = useState(false)
	const [returnDialogOpen, setReturnDialogOpen] = useState(false)
	const [raisingJiraTicket, setRaisingJiraTicket] = useState(false)
	const [raiseDialogOpen, setRaiseDialogOpen] = useState(false)
	const [refusedReason, setRefusedReason] = useState<string | null>(null)

	const ownedByMe = caseOwnershipEnabled && !!activeCase.AssigneeEmail && activeCase.AssigneeEmail === agentEmail
	const heldByAnotherAgent = caseOwnershipEnabled && !!activeCase.AssigneeEmail && !ownedByMe
	const canResolve = !caseOwnershipEnabled || ownedByMe
	const manuals = activeCase.Manuals ?? []

	// Parking a case on the client raises its ticket on its own, so pressing this
	// would only race that. The button comes back if the automation opened none.
	const parkedOnClient = activeCase.WaitParty === CLIENT_WAIT_PARTY
	const raiseBlockedReason =
		refusedReason ?? (parkedOnClient ? "Parking on the client raises the ticket automatically" : null)

	const pickUp = (email: string, name: string) => {
		setPickingUp(true)
		api.customer
			.pickUpCase(customerID, activeCase.CaseID, {AgentEmail: email, AgentName: name})
			.then(() => {
				notification.success("Case picked up")
			})
			.catch((err) => notification.error(err?.Message ?? "Failed to pick up case"))
			.finally(() => {
				setPickingUp(false)
				onPickedUp()
			})
	}

	const release = (email: string, name: string) => {
		setReleasing(true)
		api.customer
			.unassignCase(customerID, activeCase.CaseID, {AgentEmail: email, AgentName: name})
			.then(() => {
				notification.success("Case released")
				onReleased()
			})
			.catch((err) => notification.error(err?.Message ?? "Failed to release case"))
			.finally(() => setReleasing(false))
	}

	const raiseJiraTicket = (email: string, name: string) => {
		setRaisingJiraTicket(true)
		api.customer
			.raiseCaseJiraTicket(customerID, activeCase.CaseID, {AgentEmail: email, AgentName: name})
			.then((ticket) => {
				notification.success(`Jira ticket ${ticket.Key} raised`)
				onJiraTicketRaised()
			})
			.catch((err) => {
				const reason = err?.Message ?? "Failed to raise Jira ticket"
				notification.error(reason)
				// A refusal the desk decided stands however many times it is asked,
				// so the button stops offering what it cannot deliver. Anything the
				// endpoint does not call final stays pressable.
				if (err?.Retryable === false) setRefusedReason(reason)
			})
			.finally(() => {
				setRaisingJiraTicket(false)
				setRaiseDialogOpen(false)
			})
	}

	// A wait the party owes a dated response to returns the case on its own
	// deadline; only an open-ended one needs an agent to end it.
	const openEndedWait = findOpenEndedWait(activeCase, activeCase.WaitParties)

	const expiryDaysText = getExpiryText(activeCase.ExpiryDate)

	const loadNotes = useCallback(() => {
		setNotesLoading(true)
		api.customer
			.getCaseNotes(customerID, activeCase.CaseID)
			.then((data) => setNotes(data))
			.catch(() => notification.error("Failed to load notes"))
			.finally(() => setNotesLoading(false))
	}, [customerID, activeCase.CaseID, notification])

	const handleCopyLink = () => {
		const url = `${window.location.origin}/customers/${customerID}/cases?CaseID=${activeCase.CaseID}`
		navigator.clipboard.writeText(url)
		notification.success("Link copied")
	}

	const handleNoteCreated = async (noteID: string) => {
		try {
			await api.customer.attachCaseNote(customerID, activeCase.CaseID, noteID)
			notification.success("Note added")
			loadNotes()
		} catch (error: any) {
			notification.error(error.Message || "Failed to attach note to case")
		}
	}

	const noteForms = activeCase.AttachNoteForms.map((f) => ({
		Type: f.Type,
		Label: f.Label,
		Version: f.Version,
		Fields: f.Fields.map((field) => ({
			Name: field.Name,
			Type: field.Type,
			Required: field.Required,
			Options: field.Options,
		})),
		IsDefault: f.IsDefault,
	}))

	return (
		<CaseCardContainer $active>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<CaseTypeChip label={activeCase.CaseType} />
					{caseOwnershipEnabled && (
						<CaseAttentionChip case={activeCase} waitParties={activeCase.WaitParties} />
					)}
					{heldByAnotherAgent && (
						<CaseHolderChip
							icon={<PersonOutlineIcon />}
							label={`Being worked by ${activeCase.AssigneeEmail}`}
						/>
					)}
					<Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
						<AccessTimeIcon fontSize="small" />
						<Typography variant="body2" color="textSecondary">
							<Box component="span" color={colors.gray["700"]}>
								Opened
							</Box>{" "}
							{formatDistanceToNow(new Date(activeCase.SubmittedAt), {addSuffix: true})}
						</Typography>
					</Box>
					<Tooltip title="Copy link">
						<IconButton size="small" onClick={handleCopyLink}>
							<LinkIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<AddReminderButton
						customerID={customerID}
						caseID={activeCase.CaseID}
						caseType={activeCase.CaseType}
					/>
				</Box>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="flex-end"
					gap={1}
					flexWrap="wrap"
					sx={{"& > button": {whiteSpace: "nowrap"}}}
				>
					{caseOwnershipEnabled && !activeCase.AssigneeEmail && agentEmail && agentName && (
						<Button
							variant="contained"
							size="small"
							disabled={pickingUp}
							onClick={() => pickUp(agentEmail, agentName)}
							sx={resolveButtonSx}
						>
							PICK UP CASE
						</Button>
					)}
					{ownedByMe && agentEmail && agentName && (
						<Button
							variant="outlined"
							size="small"
							disabled={releasing}
							onClick={() => release(agentEmail, agentName)}
							sx={parkButtonSx}
						>
							RELEASE
						</Button>
					)}
					{ownedByMe && (
						<Button variant="outlined" size="small" onClick={() => onParkCase()} sx={parkButtonSx}>
							PARK
						</Button>
					)}
					{ownedByMe && openEndedWait && agentEmail && agentName && (
						<Button
							variant="outlined"
							size="small"
							onClick={() => setReturnDialogOpen(true)}
							sx={parkButtonSx}
						>
							RETURN
						</Button>
					)}
					{ownedByMe && (
						<Button
							variant="outlined"
							size="small"
							onClick={() => onShowManuals(activeCase.CaseType, manuals)}
							sx={parkButtonSx}
						>
							MANUALS
						</Button>
					)}
					{activeCase.JiraTicket && (
						<Tooltip title={`Open ${activeCase.JiraTicket.Key} in Jira`}>
							<Button
								variant="outlined"
								size="small"
								component="a"
								href={activeCase.JiraTicket.URL}
								target="_blank"
								rel="noopener noreferrer"
								endIcon={<OpenInNewIcon sx={{fontSize: 14}} />}
								sx={parkButtonSx}
							>
								{activeCase.JiraTicket.Key}
							</Button>
						</Tooltip>
					)}
					{ownedByMe && !activeCase.JiraTicket && agentEmail && agentName && (
						<Tooltip title={raiseBlockedReason ?? ""}>
							<span>
								<Button
									variant="outlined"
									size="small"
									disabled={raisingJiraTicket || raiseBlockedReason !== null}
									onClick={() => setRaiseDialogOpen(true)}
									sx={parkButtonSx}
								>
									{raisingJiraTicket ? "RAISING…" : "CREATE JIRA TICKET"}
								</Button>
							</span>
						</Tooltip>
					)}
					{activeCase.Resolutions.length > 0 && canResolve && (
						<Button variant="contained" size="small" onClick={() => onResolveCase()} sx={resolveButtonSx}>
							RESOLVE CASE
						</Button>
					)}
				</Box>
			</Box>
			<Typography variant="body2" color="textSecondary" mb={1}>
				<Box component="span" color={colors.gray["700"]}>
					Opened by:
				</Box>{" "}
				{activeCase.SubmitterName || activeCase.SubmitterType}
			</Typography>
			{ownedByMe && (
				<Typography variant="body2" color="textSecondary" mb={1}>
					<Box component="span" color={colors.gray["700"]}>
						Assigned to:
					</Box>{" "}
					{activeCase.AssigneeEmail}
				</Typography>
			)}
			{expiryDaysText && <DeadlineBanner>{expiryDaysText}</DeadlineBanner>}

			<Box display="flex" flexDirection="column" gap={2}>
				<CaseContextAccordion
					customerID={customerID}
					customerRef={customerRef}
					dob={dob}
					totalOutstanding={totalOutstanding}
					accounts={activeCase.Accounts.length > 0 ? activeCase.Accounts : (customerAccounts ?? [])}
				/>

				{activeCase.Accounts.length > 0 && (
					<ActiveCaseAccountsAccordion
						customerID={customerID}
						accounts={activeCase.Accounts}
						submittedAt={activeCase.SubmittedAt}
						onResolveAccount={onResolveAccount}
						canResolve={canResolve}
					/>
				)}

				<Accordion
					disableGutters
					elevation={0}
					sx={{
						border: `1px solid ${colors.gray["200"]}`,
						borderRadius: "4px",
						"&::before": {display: "none"},
					}}
					onChange={(_, expanded) => {
						if (expanded) loadNotes()
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant="body2" color="textSecondary">
							Notes · {activeCase.Notes.length} in this case
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{pt: 0}}>
						{notesLoading ? (
							<Box display="flex" flexDirection="column" gap={2}>
								{[0, 1].map((i) => (
									<Box key={i} display="flex" gap={1.5}>
										<Skeleton variant="circular" width={32} height={32} />
										<Box
											flex={1}
											p={2.5}
											border="1px solid"
											borderColor="grey.200"
											borderRadius={2}
										>
											<Box display="flex" justifyContent="space-between" mb={1.5}>
												<Skeleton variant="text" width={160} />
												<Skeleton variant="text" width={90} />
											</Box>
											<Skeleton variant="text" width="90%" />
											<Skeleton variant="text" width="75%" />
										</Box>
									</Box>
								))}
							</Box>
						) : (
							<Box display="flex" flexDirection="column" gap={2}>
								{notes.map((note) => (
									<NoteItemCard key={note.ID} note={note} onArchived={loadNotes} />
								))}
							</Box>
						)}

						{noteForms.length > 0 && (
							<Box mt={4}>
								<NoteForm
									customerID={customerID}
									forms={noteForms}
									onNoteCreated={handleNoteCreated}
									size="small"
								/>
							</Box>
						)}
					</AccordionDetails>
				</Accordion>

				<ConversationSection
					customerID={customerID}
					caseID={activeCase.CaseID}
					customerEmails={customerEmails}
					caseLabelByID={caseLabelByID}
					onPickedUp={onPickedUp}
				/>
			</Box>

			{ownedByMe && !activeCase.JiraTicket && agentEmail && agentName && (
				<RaiseJiraTicketDialog
					open={raiseDialogOpen}
					activeCase={activeCase}
					customerAccounts={customerAccounts}
					clientName={clientName}
					customerName={customerName}
					customerRef={customerRef}
					raising={raisingJiraTicket}
					onClose={() => setRaiseDialogOpen(false)}
					onConfirm={() => raiseJiraTicket(agentEmail, agentName)}
				/>
			)}

			{ownedByMe && openEndedWait && agentEmail && agentName && (
				<ReturnCaseDialog
					open={returnDialogOpen}
					customerID={customerID}
					caseID={activeCase.CaseID}
					waitParty={openEndedWait}
					agentEmail={agentEmail}
					agentName={agentName}
					onClose={() => setReturnDialogOpen(false)}
					onReturned={onReturned}
				/>
			)}
		</CaseCardContainer>
	)
}

function OpenAccountIconButton({customerID, accountID}: {customerID: string; accountID: string}) {
	const navigate = useNavigate()
	return (
		<Tooltip title="Open account">
			<IconButton
				size="small"
				onClick={() => navigate(`/customers/${customerID}/accounts?accountID=${accountID}`)}
			>
				<OpenInNewIcon sx={{fontSize: 14}} />
			</IconButton>
		</Tooltip>
	)
}

function CaseContextAccordion({
	customerID,
	customerRef,
	dob,
	totalOutstanding,
	accounts,
}: {
	customerID: string
	customerRef?: string
	dob?: string
	totalOutstanding?: string
	accounts: CustomerAccount[]
}) {
	const detailAccounts = accounts.filter(
		(account) => (account.OriginatingCreditor ?? []).length > 0 || (account.SOL ?? []).length > 0,
	)
	const hasCustomerDetails = Boolean(customerRef || dob || totalOutstanding)

	if (!hasCustomerDetails && detailAccounts.length === 0) return null

	return (
		<Accordion
			disableGutters
			elevation={0}
			sx={{
				border: `1px solid ${colors.gray["200"]}`,
				borderRadius: "4px",
				"&::before": {display: "none"},
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body2" color="textSecondary">
					Customer details
					{detailAccounts.length > 0 &&
						` · ${detailAccounts.length} account${detailAccounts.length !== 1 ? "s" : ""}`}
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				<Box display="flex" flexDirection="column" gap={1.5}>
					{hasCustomerDetails && (
						<Box
							display="grid"
							gridTemplateColumns="repeat(auto-fill, minmax(min(240px, 100%), 1fr))"
							gap={1.5}
							borderRadius={2}
							p={2}
							sx={(theme) => ({bgcolor: alpha(theme.palette.primary.main, 0.04)})}
						>
							{customerRef && (
								<CaseContextDetail label="Customer Reference" value={customerRef} emphasized />
							)}
							{dob && <CaseContextDetail label="Date of Birth" value={dob} emphasized />}
							{totalOutstanding && (
								<CaseContextDetail label="Total Outstanding" value={totalOutstanding} emphasized />
							)}
						</Box>
					)}
					{detailAccounts.length > 0 && (
						<Box
							display="grid"
							gridTemplateColumns="repeat(auto-fill, minmax(min(520px, 100%), 1fr))"
							gap={1.5}
						>
							{detailAccounts.map((account) => (
								<Box
									key={account.AccountID}
									border="1px solid"
									borderColor="grey.200"
									borderRadius={2}
									p={2}
									sx={{boxShadow: "0 1px 3px rgba(16, 24, 40, 0.08)"}}
								>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="flex-start"
										mb={1.5}
										gap={1}
									>
										<Box minWidth={0}>
											<Typography variant="caption" color="textSecondary" display="block">
												Account
											</Typography>
											<Box display="flex" alignItems="center" gap={0.5}>
												{account.Status && (
													// AccountStatusIcon only forwards fontSize for some statuses, so
													// size and spacing are set here to keep every card's header equal.
													<AccountStatusIcon
														status={account.Status}
														sx={{fontSize: 18, marginRight: 0}}
													/>
												)}
												<Typography
													variant="subtitle2"
													fontWeight={600}
													sx={{overflowWrap: "anywhere"}}
												>
													{account.AccountRef}
												</Typography>
												<CopyButton content={account.AccountRef} />
											</Box>
										</Box>
										<Box display="flex" alignItems="center" gap={1}>
											{account.SOLExpired && (
												<Chip
													label="Out of Statute"
													size="small"
													color="error"
													variant="outlined"
												/>
											)}
											<OpenAccountIconButton
												customerID={customerID}
												accountID={account.AccountID}
											/>
										</Box>
									</Box>
									<MuiDivider sx={{mb: 1.5}} />
									<Box
										display="grid"
										gridTemplateColumns="repeat(auto-fill, minmax(min(240px, 100%), 1fr))"
										gap={1.5}
									>
										{[...(account.OriginatingCreditor ?? []), ...(account.SOL ?? [])].map(
											(detail) => (
												<CaseContextDetail
													key={detail.Label}
													label={detail.Label}
													value={detail.Value}
												/>
											),
										)}
									</Box>
								</Box>
							))}
						</Box>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

function CaseContextDetail({label, value, emphasized = false}: {label: string; value: string; emphasized?: boolean}) {
	return (
		<Box>
			<Typography variant="caption" color="textSecondary" display="block">
				{label}
			</Typography>
			<Box display="flex" alignItems="center" gap={0.5}>
				<Typography
					variant={emphasized ? "body1" : "body2"}
					fontWeight={emphasized ? 600 : 500}
					sx={{overflowWrap: "anywhere"}}
				>
					{value}
				</Typography>
				<CopyButton content={value} />
			</Box>
		</Box>
	)
}

function ActiveCaseAccountsAccordion({
	customerID,
	accounts,
	submittedAt,
	onResolveAccount,
	canResolve,
}: {
	customerID: string
	accounts: ActiveCaseAccount[]
	submittedAt: string
	onResolveAccount: (account: ActiveCaseAccount) => void
	canResolve: boolean
}) {
	const resolvedCount = accounts.filter((a) => a.Resolution).length
	const total = accounts.length

	return (
		<Accordion
			defaultExpanded
			disableGutters
			elevation={0}
			sx={{
				border: `1px solid ${colors.gray["200"]}`,
				borderRadius: "4px",
				"&::before": {display: "none"},
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body2" color="textSecondary">
					{total} account{total !== 1 ? "s" : ""}{" "}
					<Box component="span" color={colors.gray["500"]}>
						({resolvedCount} resolved)
					</Box>
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				<Box display="flex" flexDirection="column" gap={1}>
					{accounts.map((a) => (
						<ActiveAccountRow
							key={a.AccountID}
							customerID={customerID}
							account={a}
							submittedAt={submittedAt}
							onResolveAccount={onResolveAccount}
							canResolve={canResolve}
						/>
					))}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

function ActiveAccountRow({
	customerID,
	account,
	submittedAt,
	onResolveAccount,
	canResolve,
}: {
	customerID: string
	account: ActiveCaseAccount
	submittedAt: string
	onResolveAccount: (account: ActiveCaseAccount) => void
	canResolve: boolean
}) {
	if (account.Resolution) {
		return <ResolvedActiveAccountRow customerID={customerID} account={account} submittedAt={submittedAt} />
	}

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				borderRadius: 2,
				boxShadow: 2,
				p: 1.5,
				backgroundColor: "white",
			}}
		>
			<Box display="flex" alignItems="center" gap={1}>
				<Typography variant="body2" fontWeight={600}>
					{account.AccountRef}
				</Typography>
				<OpenAccountIconButton customerID={customerID} accountID={account.AccountID} />
				<Typography variant="body2" color="textSecondary">
					{account.Balance}
				</Typography>
			</Box>
			{canResolve && (
				<Button
					variant="text"
					size="small"
					onClick={() => onResolveAccount(account)}
					sx={{color: colors.warmBlue["800"], fontWeight: 600}}
				>
					RESOLVE ACCOUNT
				</Button>
			)}
		</Box>
	)
}

function ResolvedActiveAccountRow({
	customerID,
	account,
	submittedAt,
}: {
	customerID: string
	account: ActiveCaseAccount
	submittedAt: string
}) {
	const resolvedAgoText = account.ResolutionAt
		? formatDistanceToNow(new Date(account.ResolutionAt), {addSuffix: true})
		: ""
	const resolutionDurationText = account.ResolutionAt
		? formatDistanceStrict(new Date(submittedAt), new Date(account.ResolutionAt))
		: ""

	return (
		<Accordion
			disableGutters
			elevation={2}
			sx={{
				borderRadius: "4px",
				backgroundColor: colors.gray["50"],
				"&::before": {display: "none"},
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Box display="flex" alignItems="center" justifyContent="space-between" flex={1} pr={1}>
					<Box>
						<Box display="flex" alignItems="center" gap={0.5}>
							<Typography variant="body2" fontWeight={600}>
								{account.AccountRef}
							</Typography>
							<OpenAccountIconButton customerID={customerID} accountID={account.AccountID} />
						</Box>
						<Typography variant="body2" color="textSecondary">
							{account.Balance}
						</Typography>
					</Box>
					{account.Resolution && (
						<Box display="flex" alignItems="center" gap={1}>
							<ResolutionChip
								resolution={account.Resolution}
								isTerminal={account.IsTerminal}
								reason={account.Reason}
							/>
							{account.Resolution !== "Withdrawn" && account.Reason && (
								<Typography variant="body2" color="textSecondary">
									{account.Reason}
								</Typography>
							)}
						</Box>
					)}
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				<Box display="flex" flexDirection="column" gap={0.5}>
					{(account.ResolverName || account.ResolverType) && (
						<Typography variant="body2" color="textSecondary" component="div">
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								<Box component="span" color={colors.gray["700"]}>
									Resolved by:
								</Box>
								<span>{account.ResolverName || account.ResolverType}</span>
								{account.ResolverEmail && (
									<Tooltip title="Copy email">
										<IconButton
											size="small"
											onClick={() => navigator.clipboard.writeText(account.ResolverEmail!)}
											sx={{p: 0.25}}
										>
											<ContentCopyIcon sx={{fontSize: 14}} />
										</IconButton>
									</Tooltip>
								)}
							</Box>
						</Typography>
					)}
					{resolvedAgoText && (
						<Typography variant="body2" color="textSecondary">
							{resolvedAgoText}
							{resolutionDurationText && ` · took ${resolutionDurationText}`}
						</Typography>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

function ClosedCaseCard({
	customerID,
	closedCase,
	onArchived,
}: {
	customerID: string
	closedCase: ClosedCase
	onArchived: () => void
}) {
	const {notification} = useNotification()
	const [notes, setNotes] = useState<CaseNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)

	const loadNotes = useCallback(() => {
		setNotesLoading(true)
		api.customer
			.getCaseNotes(customerID, closedCase.CaseID)
			.then((data) => setNotes(data))
			.catch(() => notification.error("Failed to load notes"))
			.finally(() => setNotesLoading(false))
	}, [customerID, closedCase.CaseID, notification])

	const handleCopyLink = () => {
		const url = `${window.location.origin}/customers/${customerID}/cases?CaseID=${closedCase.CaseID}`
		navigator.clipboard.writeText(url)
		notification.success("Link copied")
	}

	const timelineEntries: TimelineEntry[] = []
	if (closedCase.SubmittedAt) {
		timelineEntries.push({label: "Opened at", at: closedCase.SubmittedAt})
	}
	if (closedCase.ClosedAt) {
		timelineEntries.push({label: "Resolved at", at: closedCase.ClosedAt})
	}

	return (
		<CaseCardContainer>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<ClosedCaseTypeChip label={closedCase.CaseType} />
					{closedCase.Resolution && (
						<ResolutionChip resolution={closedCase.Resolution} isTerminal={closedCase.IsTerminal} />
					)}
					<Tooltip title="Copy link">
						<IconButton size="small" onClick={handleCopyLink}>
							<LinkIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
				{closedCase.Reason && (
					<Typography variant="body2" color="textSecondary">
						{closedCase.Reason}
					</Typography>
				)}
			</Box>
			<Typography variant="body2" color="textSecondary">
				<Box component="span" color={colors.gray["700"]}>
					Opened by:
				</Box>{" "}
				{closedCase.SubmitterName || closedCase.SubmitterType}
			</Typography>
			{(closedCase.ResolverName || closedCase.ResolverType) && (
				<Typography variant="body2" color="textSecondary" mb={1}>
					<Box component="span" color={colors.gray["700"]}>
						Resolved by:
					</Box>{" "}
					{closedCase.ResolverName || closedCase.ResolverType}
				</Typography>
			)}
			<Accordion
				disableGutters
				elevation={0}
				sx={{border: `1px solid ${colors.gray["200"]}`, borderRadius: "4px", "&::before": {display: "none"}}}
				onChange={(_, expanded) => {
					if (expanded) loadNotes()
				}}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="body2" color="textSecondary">
						{closedCase.Notes.length} note{closedCase.Notes.length !== 1 ? "s" : ""}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{pt: 0}}>
					{notesLoading ? (
						<Box display="flex" flexDirection="column" gap={2}>
							{[0, 1].map((i) => (
								<Box key={i} display="flex" gap={1.5}>
									<Skeleton variant="circular" width={32} height={32} />
									<Box flex={1} p={2.5} border="1px solid" borderColor="grey.200" borderRadius={2}>
										<Box display="flex" justifyContent="space-between" mb={1.5}>
											<Skeleton variant="text" width={160} />
											<Skeleton variant="text" width={90} />
										</Box>
										<Skeleton variant="text" width="90%" />
										<Skeleton variant="text" width="75%" />
									</Box>
								</Box>
							))}
						</Box>
					) : (
						<Box display="flex" flexDirection="column" gap={2}>
							{notes.map((note) => (
								<NoteItemCard key={note.ID} note={note} onArchived={onArchived} />
							))}
						</Box>
					)}
				</AccordionDetails>
			</Accordion>
			<TimelineAccordion entries={timelineEntries} />
		</CaseCardContainer>
	)
}

function ResolvedAccountCaseCard({
	customerID,
	resolvedCase,
	onArchived,
}: {
	customerID: string
	resolvedCase: ResolvedAccountCase
	onArchived: () => void
}) {
	const {notification} = useNotification()
	const [notes, setNotes] = useState<CaseNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)

	const loadNotes = useCallback(() => {
		setNotesLoading(true)
		api.customer
			.getCaseNotes(customerID, resolvedCase.CaseID)
			.then((data) => setNotes(data))
			.catch(() => notification.error("Failed to load notes"))
			.finally(() => setNotesLoading(false))
	}, [customerID, resolvedCase.CaseID, notification])

	const handleCopyLink = () => {
		const url = `${window.location.origin}/customers/${customerID}/cases?CaseID=${resolvedCase.CaseID}`
		navigator.clipboard.writeText(url)
		notification.success("Link copied")
	}

	const timelineEntries: TimelineEntry[] = []
	if (resolvedCase.SubmittedAt) {
		timelineEntries.push({label: "Opened at", at: resolvedCase.SubmittedAt})
	}
	resolvedCase.Accounts.filter((a) => a.ResolutionAt)
		.slice()
		.sort((a, b) => new Date(a.ResolutionAt).getTime() - new Date(b.ResolutionAt).getTime())
		.forEach((a) => {
			timelineEntries.push({
				label: "Account resolved at",
				at: a.ResolutionAt,
				detail: a.AccountRef,
			})
		})
	if (resolvedCase.ClosedAt) {
		timelineEntries.push({label: "Closed at", at: resolvedCase.ClosedAt})
	}

	return (
		<CaseCardContainer>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<ClosedCaseTypeChip label={resolvedCase.CaseType} />
					<Tooltip title="Copy link">
						<IconButton size="small" onClick={handleCopyLink}>
							<LinkIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
			<Typography variant="body2" color="textSecondary" mb={1}>
				<Box component="span" color={colors.gray["700"]}>
					Opened by:
				</Box>{" "}
				{resolvedCase.SubmitterName || resolvedCase.SubmitterType}
			</Typography>

			<Box display="flex" flexDirection="column" gap={1} mb={1}>
				{resolvedCase.Accounts.map((a) => (
					<ResolvedAccountRow
						key={a.AccountID}
						customerID={customerID}
						account={a}
						submittedAt={resolvedCase.SubmittedAt}
					/>
				))}
			</Box>

			<Accordion
				disableGutters
				elevation={0}
				sx={{border: `1px solid ${colors.gray["200"]}`, borderRadius: "4px", "&::before": {display: "none"}}}
				onChange={(_, expanded) => {
					if (expanded) loadNotes()
				}}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="body2" color="textSecondary">
						{resolvedCase.Notes.length} note{resolvedCase.Notes.length !== 1 ? "s" : ""}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{pt: 0}}>
					{notesLoading ? (
						<Box display="flex" flexDirection="column" gap={2}>
							{[0, 1].map((i) => (
								<Box key={i} display="flex" gap={1.5}>
									<Skeleton variant="circular" width={32} height={32} />
									<Box flex={1} p={2.5} border="1px solid" borderColor="grey.200" borderRadius={2}>
										<Box display="flex" justifyContent="space-between" mb={1.5}>
											<Skeleton variant="text" width={160} />
											<Skeleton variant="text" width={90} />
										</Box>
										<Skeleton variant="text" width="90%" />
										<Skeleton variant="text" width="75%" />
									</Box>
								</Box>
							))}
						</Box>
					) : (
						<Box display="flex" flexDirection="column" gap={2}>
							{notes.map((note) => (
								<NoteItemCard key={note.ID} note={note} onArchived={onArchived} />
							))}
						</Box>
					)}
				</AccordionDetails>
			</Accordion>
			<TimelineAccordion entries={timelineEntries} />
		</CaseCardContainer>
	)
}

function ResolvedAccountRow({
	customerID,
	account,
	submittedAt,
}: {
	customerID: string
	account: ResolvedCaseAccount
	submittedAt: string
}) {
	const resolvedAgoText = account.ResolutionAt
		? formatDistanceToNow(new Date(account.ResolutionAt), {addSuffix: true})
		: ""
	const resolutionDurationText = account.ResolutionAt
		? formatDistanceStrict(new Date(submittedAt), new Date(account.ResolutionAt))
		: ""

	return (
		<Accordion disableGutters elevation={2} sx={{borderRadius: "4px", "&::before": {display: "none"}}}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Box display="flex" alignItems="center" justifyContent="space-between" flex={1} pr={1}>
					<Box>
						<Box display="flex" alignItems="center" gap={0.5}>
							<Typography variant="body2" fontWeight={600}>
								{account.AccountRef}
							</Typography>
							<OpenAccountIconButton customerID={customerID} accountID={account.AccountID} />
							{account.ClosedStatus && (
								<RedStatusChip label={account.ClosedStatus} size="small" variant="outlined" />
							)}
						</Box>
						<Typography variant="body2" color="textSecondary">
							{account.Balance}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<ResolutionChip
							resolution={account.Resolution}
							isTerminal={account.IsTerminal}
							reason={account.Reason}
						/>
						{account.Resolution !== "Withdrawn" && account.Reason && (
							<Typography variant="body2" color="textSecondary">
								{account.Reason}
							</Typography>
						)}
					</Box>
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				<Box display="flex" flexDirection="column" gap={0.5}>
					{(account.ResolverName || account.ResolverType) && (
						<Typography variant="body2" color="textSecondary" component="div">
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								<Box component="span" color={colors.gray["700"]}>
									Resolved by:
								</Box>
								<span>{account.ResolverName || account.ResolverType}</span>
								{account.ResolverEmail && (
									<Tooltip title="Copy email">
										<IconButton
											size="small"
											onClick={() => navigator.clipboard.writeText(account.ResolverEmail!)}
											sx={{p: 0.25}}
										>
											<ContentCopyIcon sx={{fontSize: 14}} />
										</IconButton>
									</Tooltip>
								)}
							</Box>
						</Typography>
					)}
					{resolvedAgoText && (
						<Typography variant="body2" color="textSecondary">
							{resolvedAgoText}
							{resolutionDurationText && ` · took ${resolutionDurationText}`}
						</Typography>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

type TimelineEntry = {
	label: string
	at: string
	detail?: string
}

function TimelineAccordion({entries}: {entries: TimelineEntry[]}) {
	if (entries.length === 0) return null
	return (
		<Accordion
			disableGutters
			elevation={0}
			sx={{
				border: `1px solid ${colors.gray["200"]}`,
				borderRadius: "4px",
				"&::before": {display: "none"},
				mt: 1,
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body2" color="textSecondary">
					Timeline
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				<Box display="flex" flexDirection="column" gap={0.5}>
					{entries.map((e, i) => (
						<Typography key={`${e.label}-${e.at}-${i}`} variant="body2" color="textSecondary">
							<Box component="span" color={colors.gray["700"]}>
								{e.label}:
							</Box>{" "}
							{format(new Date(e.at), "d MMM yyyy, HH:mm")}
							{e.detail && ` · ${e.detail}`}
						</Typography>
					))}
				</Box>
			</AccordionDetails>
		</Accordion>
	)
}

function getExpiryText(expiryDate: string): string | null {
	if (!expiryDate) return null
	const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
	if (days < 0) return "Expired"
	if (days === 0) return "Expires today"
	return `Expires in ${days} day${days !== 1 ? "s" : ""}`
}

// Styles
const parkButtonSx = {
	color: colors.warmBlue["900"],
	borderColor: colors.warmBlue["900"],
	textTransform: "none" as const,
}

const resolveButtonSx = {
	backgroundColor: colors.warmBlue["900"],
	"&:hover": {backgroundColor: colors.warmBlue["800"]},
	textTransform: "none" as const,
}

const CaseCardContainer = styled.div<{$active?: boolean}>`
	border-radius: 8px;
	padding: 16px;
	box-shadow: ${(props) =>
		props.$active
			? `0px 3px 3px -2px rgba(66, 103, 178, 0.2),
			   0px 4px 6px 0px rgba(66, 103, 178, 0.14),
			   0px 2px 8px 1px rgba(66, 103, 178, 0.12)`
			: `0px 3px 1px -2px rgba(0, 0, 0, 0.2),
			   0px 2px 2px 0px rgba(0, 0, 0, 0.14),
			   0px 1px 5px 0px rgba(0, 0, 0, 0.12)`};
`

const CaseTypeChip = styled(Chip).attrs({size: "small", variant: "outlined"})`
	&& {
		background-color: ${colors.warning["50"]};
		border-color: ${colors.warning["200"]};
		color: ${colors.warning["800"]};
	}
	font-weight: 500;
`

const ClosedCaseTypeChip = styled(Chip).attrs({size: "small", variant: "outlined"})`
	border-color: ${colors.gray["300"]};
	color: ${colors.gray["600"]};
	font-weight: 500;
`

const CaseHolderChip = styled(Chip).attrs({size: "small", variant: "outlined"})`
	&& {
		background-color: ${colors.nobleKnight["25"]};
		border-color: ${colors.nobleKnight["200"]};
		color: ${colors.nobleKnight["800"]};
	}
	&& .MuiChip-icon {
		color: ${colors.nobleKnight["600"]};
	}
	font-weight: 500;
`

function ResolutionChip({
	resolution,
	isTerminal,
	reason,
}: {
	resolution: string
	isTerminal: boolean
	reason?: string | null
}) {
	const label = resolution === "Withdrawn" && reason ? `${resolution} - ${reason}` : resolution
	if (resolution === "Withdrawn") {
		return <GrayStatusChip label={label} size="small" variant="outlined" />
	}
	if (isTerminal) {
		return <RedStatusChip label={label} size="small" variant="outlined" />
	}
	return <GreenStatusChip label={label} size="small" variant="outlined" />
}

const GreenStatusChip = styled(Chip)`
	&& {
		background-color: ${colors.success["50"]};
		border-color: ${colors.success["200"]};
		color: ${colors.success["700"]};
	}
	font-weight: 500;
`

const RedStatusChip = styled(Chip)`
	&& {
		background-color: ${colors.error["50"]};
		border-color: ${colors.error["200"]};
		color: ${colors.error["700"]};
	}
	font-weight: 500;
`

const GrayStatusChip = styled(Chip)`
	&& {
		background-color: ${colors.gray["50"]};
		border-color: ${colors.gray["300"]};
		color: ${colors.gray["600"]};
	}
	font-weight: 500;
`

const DeadlineBanner = styled.div`
	background-color: ${colors.blue["50"]};
	color: ${colors.blue["700"]};
	padding: 8px 12px;
	border-radius: 4px;
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 8px;
`

const Divider = styled.hr`
	border: none;
	border-top: 1px solid ${colors.gray["200"]};
	margin: 16px 0;
`

const CaseTypeMenuItem = styled(MenuItem)`
	display: flex;
	justify-content: space-between;
	min-width: 200px;
`

const KeyboardArrowRightIcon = styled.span`
	color: ${colors.gray["400"]};
	margin-left: 8px;
`

const highlightBlink = keyframes`
	0%, 100% { background-color: transparent; }
	50% { background-color: ${colors.warmBlue["50"]}; }
`

const CaseCardWrapper = styled.div<{$highlight: boolean; $phase: "blink" | "settled"}>`
	border-radius: 8px;
	animation: ${(props) => (props.$highlight && props.$phase === "blink" ? highlightBlink : "none")} 1.2s ease-in-out 2;

	${(props) =>
		props.$highlight &&
		props.$phase === "settled" &&
		`
		& > ${CaseCardContainer} {
			box-shadow:
				0px 7px 8px -4px rgba(66, 103, 178, 0.3),
				0px 12px 17px 2px rgba(66, 103, 178, 0.22),
				0px 5px 22px 4px rgba(66, 103, 178, 0.19);
		}
	`}
`

export {Cases}
